import {
  ApplicationRef,
  ComponentRef,
  EnvironmentInjector,
  Injectable,
  createComponent,
  inject,
} from '@angular/core';
import { SessionExpiredOverlayComponent } from '../shared/session-expired-overlay/session-expired-overlay.component';

/**
 * Mount programmatico dell'overlay "sessione scaduta" sopra <body>.
 *
 * Uso tipico (AuthSessionService.clearSessionAndGoToLogin):
 *   await this._sessionExpired.show(4);
 *   await this._router.navigate(['/login']);
 *
 * La Promise risolve quando il countdown termina o l'utente clicca la CTA.
 * Idempotente: se l'overlay e gia mostrato, chiamate concorrenti ricevono
 * la stessa Promise (evita doppio-mount su 401 multipli paralleli).
 */
@Injectable({ providedIn: 'root' })
export class SessionExpiredService {
  private appRef = inject(ApplicationRef);
  private envInjector = inject(EnvironmentInjector);

  private currentRef: ComponentRef<SessionExpiredOverlayComponent> | null = null;
  private pendingPromise: Promise<void> | null = null;
  private pendingResolve: (() => void) | null = null;

  show(countdownSeconds = 4): Promise<void> {
    if (this.pendingPromise) {
      return this.pendingPromise;
    }

    this.pendingPromise = new Promise<void>(resolve => {
      this.pendingResolve = resolve;
    });

    const componentRef = createComponent(SessionExpiredOverlayComponent, {
      environmentInjector: this.envInjector,
    });
    componentRef.setInput('countdownSeconds', countdownSeconds);
    componentRef.instance.redirect.subscribe(() => {
      const resolve = this.pendingResolve;
      this.dispose();
      resolve?.();
    });

    document.body.appendChild(componentRef.location.nativeElement);
    this.appRef.attachView(componentRef.hostView);
    this.currentRef = componentRef;

    return this.pendingPromise;
  }

  /** Cleanup: detach view + destroy component, reset stato interno. */
  private dispose(): void {
    if (this.currentRef) {
      this.appRef.detachView(this.currentRef.hostView);
      this.currentRef.destroy();
      this.currentRef = null;
    }
    this.pendingPromise = null;
    this.pendingResolve = null;
  }
}
